import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const StatCard = ({ label, value, note, testid }) => (
  <div
    data-testid={testid}
    className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6 shadow-[0_12px_30px_rgba(23,63,53,0.07)]"
  >
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-taupe">{label}</p>
    <p className="mt-2 font-serif text-4xl text-forest">{value}</p>
    {note && <p className="mt-2 text-xs leading-relaxed text-taupe">{note}</p>}
  </div>
);

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    document.title = "Admin Overview | Rooted Healing";
  }, []);

  useEffect(() => {
    setData(null);
    api.get(`/admin/overview?days=${days}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [days]);

  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  const maxMonthly = Math.max(1, ...data.monthly_growth.map((m) => m.count));
  const maxDaily = Math.max(1, ...data.registrations_by_day.map((d) => d.count));

  return (
    <div data-testid="admin-overview">
      <h1 className="font-serif text-3xl text-forest">Overview</h1>
      <p className="mt-2 text-sm text-taupe">
        Real recorded data only. Nothing here is invented or estimated.
      </p>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Registered members" value={data.total_members} testid="stat-members" />
        <StatCard label="Completed discoveries" value={data.total_assessments} testid="stat-assessments" />
        <StatCard
          label="Completion rate"
          value={data.completion_rate != null ? `${data.completion_rate}%` : "n/a"}
          note="Completed discoveries vs. unfinished saved drafts"
          testid="stat-completion"
        />
        <StatCard
          label="Returning members"
          value={data.returning_members}
          note="Members with two or more completed discoveries"
          testid="stat-returning"
        />
        <StatCard label="Newsletter subscribers" value={data.newsletter_subscribers} testid="stat-subscribers" />
        <StatCard
          label="Consultation link clicks"
          value={data.booking_clicks}
          note={data.booking_note}
          testid="stat-booking-clicks"
        />
        <StatCard
          label="Discoveries started"
          value={data.events_by_type?.assessment_started ?? 0}
          testid="stat-started"
        />
        <StatCard
          label="Discoveries completed"
          value={data.events_by_type?.assessment_completed ?? 0}
          testid="stat-completed-events"
        />
        <StatCard
          label="Repeat discoveries"
          value={data.repeat_assessments}
          note="Completed discoveries beyond each member's first"
          testid="stat-repeat"
        />
        <StatCard
          label="Discovery to booking click"
          value={data.discovery_to_booking_click?.members_with_discovery_and_click ?? 0}
          note={data.discovery_to_booking_click?.note}
          testid="stat-conversion"
        />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif text-xl text-forest">New registrations</h2>
            <div className="flex gap-2">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  data-testid={`overview-days-${d}`}
                  onClick={() => setDays(d)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                    days === d ? "bg-forest text-ivory" : "border border-forest/20 text-forest hover:border-gold"
                  }`}
                >
                  {d} days
                </button>
              ))}
            </div>
          </div>
          {data.registrations_by_day.length === 0 ? (
            <p className="mt-4 text-sm text-taupe">No new registrations in this window yet.</p>
          ) : (
            <div className="mt-5 flex h-32 items-end gap-1.5">
              {data.registrations_by_day.map((d) => (
                <div key={d.date} className="group flex flex-1 flex-col items-center gap-1">
                  <span className="text-[10px] text-taupe opacity-0 transition-opacity group-hover:opacity-100">
                    {d.count}
                  </span>
                  <div
                    className="w-full rounded-t-md bg-forest/80"
                    style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: 4 }}
                    title={`${d.date}: ${d.count}`}
                  />
                  <span className="text-[9px] text-taupe">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-7">
          <h2 className="font-serif text-xl text-forest">Membership growth by month</h2>
          {data.monthly_growth.length === 0 ? (
            <p className="mt-4 text-sm text-taupe">No members yet.</p>
          ) : (
            <div className="mt-5 flex h-32 items-end gap-2">
              {data.monthly_growth.map((m) => (
                <div key={m.month} className="group flex flex-1 flex-col items-center gap-1">
                  <span className="text-[10px] text-taupe opacity-0 transition-opacity group-hover:opacity-100">
                    {m.count}
                  </span>
                  <div
                    className="w-full rounded-t-md bg-gold"
                    style={{ height: `${(m.count / maxMonthly) * 100}%`, minHeight: 4 }}
                    title={`${m.month}: ${m.count}`}
                  />
                  <span className="text-[9px] text-taupe">{m.month.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
